import { Card } from "@/components/ui/card";
import { Navigation } from "@/components/Navigation";
import { ProfileForm } from "@/components/settings/ProfileForm";
import { BibleVersionSelect } from "@/components/settings/BibleVersionSelect";
import { useSession } from "@supabase/auth-helpers-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ProfileHeader } from "@/components/settings/ProfileHeader";
import { SubscriptionBadge } from "@/components/settings/SubscriptionBadge";
import { AdminSection } from "@/components/settings/AdminSection";
import { AboutSection } from "@/components/settings/AboutSection";
import { AdminToggle } from "@/components/settings/AdminToggle";
import { AdminNavigation } from "@/components/admin/AdminNavigation";
import { useToast } from "@/hooks/use-toast";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { useTheme } from "@/components/ui/theme-provider";
import { SubscriptionManagement } from "@/components/settings/SubscriptionManagement";
import { SupportSection } from "@/components/settings/SupportSection";

const Settings = () => {
  const session = useSession();
  const { toast } = useToast();
  const { theme } = useTheme();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminView, setIsAdminView] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>("free");
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Ensure the page scrolls to top when mounted
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch profile data and check admin status
  useEffect(() => {
    const fetchProfileAndCheckAdmin = async () => {
      if (!session?.user?.id) {
        setIsLoading(false);
        return;
      }
      
      try {
        // Fetch profile data
        const { data: profile } = await supabase
          .from('profiles')
          .select('avatar_url, subscription_status')
          .eq('id', session.user.id)
          .single();
        
        if (profile?.avatar_url) {
          setAvatarUrl(profile.avatar_url);
        }
        if (profile?.subscription_status) {
          setSubscriptionStatus(profile.subscription_status);
        }
        
        // Check admin status
        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .eq('role', 'admin')
          .maybeSingle();
        
        if (data && !error) {
          console.log("Admin user confirmed in Settings:", session.user.id);
          setIsAdmin(true);
          
          // Initialize admin view state from session storage
          const storedAdminView = sessionStorage.getItem('adminViewEnabled');
          setIsAdminView(storedAdminView === 'true');
          
          // Default to admin view for admin users if not set yet
          if (storedAdminView === null) {
            sessionStorage.setItem('adminViewEnabled', 'true');
            setIsAdminView(true);
          }
        } else {
          console.log("User is not an admin or error occurred:", error);
          setIsAdmin(false);
          // Clear admin view if user is not an admin
          sessionStorage.removeItem('adminViewEnabled');
          setIsAdminView(false);
        }
      } catch (error) {
        console.error("Error fetching profile data:", error);
        toast({
          title: "Error",
          description: "Failed to load profile data",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileAndCheckAdmin();
  }, [session, toast]);

  // Update isAdminView whenever sessionStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      const adminViewEnabled = sessionStorage.getItem('adminViewEnabled') === 'true';
      setIsAdminView(adminViewEnabled);
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also check on mount
    handleStorageChange();
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app-background transition-colors duration-300">
        <Card className="p-6 bg-app-surface border-app-border">
          <p className="text-app-text-secondary">Loading settings...</p>
        </Card>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app-background transition-colors duration-300">
        <Card className="p-6 bg-app-surface border-app-border">
          <p className="text-app-text-secondary">Please sign in to access settings.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app-background transition-colors duration-300">
      <div className="container py-8 pb-24">
        <div className="flex flex-col items-center mb-8">
          <img
            src="/lovable-uploads/5c561338-e504-4c6d-810f-f9a17db22224.png"
            alt="Grace Awakens Logo"
            className="w-32 h-32 mb-4"
            loading="eager"
          />
          <h1 className="text-3xl font-semibold text-app-text-primary transition-colors duration-300">Settings</h1>
          
          <ProfileHeader 
            session={session}
            avatarUrl={avatarUrl}
            isUploading={isUploading}
          />
        </div>

        <Card className="max-w-md mx-auto p-6 bg-app-surface/80 backdrop-blur-sm border-app-border space-y-6 transition-colors duration-300">
          {/* Admin Toggle always visible for admin users */}
          {isAdmin && (
            <AdminToggle isAdmin={isAdmin} />
          )}
          
          {/* Subscription Section - show badge and management together */}
          {(!isAdmin || !isAdminView) && (
            <>
              <SubscriptionBadge status={subscriptionStatus} isAdmin={isAdmin} />
              
              {/* Subscription Management - show prominently for paid subscribers */}
              {session?.user && (subscriptionStatus === 'premium' || subscriptionStatus === 'church') && (
                <SubscriptionManagement userId={session.user.id} status={subscriptionStatus} />
              )}
            </>
          )}
          
          {/* Show admin section when in admin view */}
          {isAdmin && isAdminView ? (
            <AdminSection isAdmin={isAdmin} />
          ) : (
            <div className="space-y-6">
              <BibleVersionSelect />
              <ProfileForm />
              <SupportSection />
              <AboutSection />
            </div>
          )}

          {/* Theme Toggle moved to bottom */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-app-text-secondary transition-colors duration-300">Appearance</h3>
            <ThemeToggle />
          </div>
        </Card>
      </div>
      
      {/* Show admin navigation when in admin view */}
      {isAdmin && isAdminView ? <AdminNavigation /> : <Navigation />}
    </div>
  );
};

export default Settings;
